type RouteLocation = {
  pathname: string;
  search?: string;
  hash?: string;
  state?: unknown;
};

export type ListingReturnLocation = {
  pathname: string;
  search: string;
  hash: string;
  state?: unknown;
};

export type ProfileLocationState = {
  listingReturnLocation: ListingReturnLocation;
  /** Number of history entries between the original listing and this route. */
  profileHistoryDepth: number;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isListingPath = (pathname: string) => pathname.startsWith('/listing/');

const captureListingLocation = (
  location: RouteLocation,
): ListingReturnLocation | null => {
  if (!isListingPath(location.pathname)) return null;

  return {
    pathname: location.pathname,
    search: location.search ?? '',
    hash: location.hash ?? '',
    state: location.state,
  };
};

/**
 * Preserve the listing route beneath Profile. Other origins keep the existing
 * profile behavior and do not receive navigation state.
 */
export const createProfileLocationState = (
  location: RouteLocation,
): ProfileLocationState | undefined => {
  const listingReturnLocation = captureListingLocation(location);
  return listingReturnLocation
    ? { listingReturnLocation, profileHistoryDepth: 1 }
    : undefined;
};

/** Read and validate browser history state before using it as a return route. */
export const getListingReturnLocation = (
  state: unknown,
): ListingReturnLocation | null => {
  if (!isObject(state) || !isObject(state.listingReturnLocation)) return null;

  const candidate = state.listingReturnLocation;
  if (
    typeof candidate.pathname !== 'string' ||
    !isListingPath(candidate.pathname) ||
    typeof candidate.search !== 'string' ||
    typeof candidate.hash !== 'string'
  ) {
    return null;
  }

  return candidate as ListingReturnLocation;
};

/** Read the complete state needed to return through the browser history. */
export const getProfileLocationState = (
  state: unknown,
): ProfileLocationState | undefined => {
  if (!isObject(state)) return undefined;
  const listingReturnLocation = getListingReturnLocation(state);
  const profileHistoryDepth = state.profileHistoryDepth;
  if (
    !listingReturnLocation ||
    !Number.isInteger(profileHistoryDepth) ||
    (profileHistoryDepth as number) < 1
  ) {
    return undefined;
  }

  return {
    listingReturnLocation,
    profileHistoryDepth: profileHistoryDepth as number,
  };
};

/**
 * Resolve the listing layer from either its own route or the validated listing
 * stored beneath a profile route.
 */
export const getListingLayerLocation = (
  location: RouteLocation,
  isProfileRoute: boolean,
): ListingReturnLocation | null => {
  const currentListing = captureListingLocation(location);
  if (currentListing) return currentListing;
  return isProfileRoute
    ? (getProfileLocationState(location.state)?.listingReturnLocation ?? null)
    : null;
};

/** Keep the listing origin and advance its depth for a pushed profile route. */
export const getNextProfileLocationState = (
  state: unknown,
): ProfileLocationState | undefined => {
  const current = getProfileLocationState(state);
  return current
    ? {
        ...current,
        profileHistoryDepth: current.profileHistoryDepth + 1,
      }
    : undefined;
};

/** Pop every Profile entry and reveal the original listing history entry. */
export const getProfileCloseHistoryDelta = (state: unknown): number | null => {
  const current = getProfileLocationState(state);
  return current ? -current.profileHistoryDepth : null;
};
